import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, message } from "antd";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { lookupBookingByRefV2 } from "hooks/api/booking-v2";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { BookingStatus } from "shared/types/enum";
import { buildOpenBookingResumeDraft } from "shared/utils/scheduleResumeDraft";

// Shared booking-ref search box, mounted on the Jadwal page (PageHeading
// extra slot), the schedule list view and the Dashboard's "Hari Ini" row. On
// a match it deep-links to the booking's schedule occurrence and reopens the
// detail drawer there (see ScheduleDetails.tsx / ScheduleBooking.tsx's
// resumeBookingId handling).
//
// Visual: one quiet "glass" pill (translucent white over the page ground,
// hairline edge, no separate button); the magnifier sits inside as a prefix
// and swaps to a spinner while a lookup runs. Enter submits; the clear
// affordance comes from AntD's allowClear. Styling lives in index.css under
// `.booking-ref-search` (hover / focus-within states).
const BookingRefSearch = ({
  style,
  inputStyle,
}: {
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("");

  const handleSearch = async (ref: string) => {
    const trimmed = ref.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    try {
      const result = await lookupBookingByRefV2(trimmed);
      if (!result) {
        message.error(translate("messages.error.booking_ref_not_found"));
        return;
      }
      navigate(
        `${PATHS.SCHEDULE}/${result.schedule_id}?occurrence_timestamp=${encodeURIComponent(result.occurrence_timestamp)}`,
        {
          state: {
            scheduleDraft: buildOpenBookingResumeDraft(
              location.pathname,
              result.id,
              result.status === BookingStatus.Waitlist ||
                result.status === BookingStatus.WaitlistExpired,
            ),
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="search"
      className="booking-ref-search"
      style={{ width: 240, ...style, ...inputStyle }}
    >
      <span className="booking-ref-search-icon" aria-hidden>
        {loading ? <LoadingOutlined spin /> : <SearchOutlined />}
      </span>
      <Input
        variant="borderless"
        placeholder={translate("form.placeholders.booking_ref_search")}
        aria-label={translate("form.placeholders.booking_ref_search")}
        allowClear
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={() => handleSearch(value)}
        disabled={loading}
        style={{ color: colors.ink }}
      />
    </div>
  );
};

export default BookingRefSearch;
